import {
    ComponentRef,
    Directive,
    Input,
    ViewContainerRef,
    inject,
    Output,
    EventEmitter,
    OnInit,
    OnDestroy,
    ElementRef
} from '@angular/core';
import {NlrDropdownComponent} from './dropdown/nlr-dropdown.component';
import {OperatorFunction, of, Subject, fromEvent, takeUntil, debounceTime, distinctUntilChanged, delay} from 'rxjs';
import {NgControl} from '@angular/forms';
import {filter} from "rxjs/operators";

@Directive({
    selector: '[nlrTypehead]',
    standalone: true
})
export class NlrTypeheadDirective implements OnInit, OnDestroy {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Input() nlrTypehead: OperatorFunction<string, readonly any[]> | null | undefined;
    @Output() itemSelected? = new EventEmitter<any>();
    viewContainerRef = inject(ViewContainerRef);
    dropdownViewRefIndex = -1;
    dropdownRef: ComponentRef<NlrDropdownComponent>;
    destroy = new Subject<void>();
    typeaheadChanged = false;

    constructor(private readonly host: ElementRef, private readonly ngControl: NgControl) {}

    ngOnInit(): void {
        // handle navigation on key down to avoid browser issues
        fromEvent<KeyboardEvent>(this.host.nativeElement, 'keydown')
            .pipe(
                filter(event => ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'].indexOf(event.key) !== -1),
                takeUntil(this.destroy)
            )
            .subscribe(event => {
                if (this.dropdownRef?.instance.items &&
                    this.dropdownRef.instance.items.length > 0) {
                    if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        this.dropdownRef.instance.highlightNext();
                    } else if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        this.dropdownRef.instance.highlightPrevious();
                    } else if (event.key === 'Enter' || event.key === 'Tab') {
                        event.preventDefault();
                        this.dropdownRef.instance.selectCurrentItem();
                    } else if (event.key === 'Escape') {
                        event.preventDefault();
                        this.ngControl.control.setValue('');
                        this.hideDropdownMenu();
                    }
                }
            });

        fromEvent<KeyboardEvent>(this.host.nativeElement, 'input')
            .pipe(
                debounceTime(100),
                distinctUntilChanged(),
                takeUntil(this.destroy)
            )
            .subscribe(() => {
                const controlValue = this.ngControl.value;

                if (controlValue) {
                    this.typeaheadChanged = true;
                    if (this.dropdownViewRefIndex < 0) {
                        this.createNewDropdownAndInsertIntoContainer();
                    }
                    if (this.nlrTypehead) {
                        this.nlrTypehead(of(controlValue)).subscribe({
                            next: values => {
                                if (this.dropdownRef) {
                                    this.dropdownRef.setInput('items', [...values]);
                                    this.dropdownRef.setInput('mark', controlValue);
                                }
                            }
                        });
                    }
                } else {
                    this.hideDropdownMenu();
                }
            });

        fromEvent<KeyboardEvent>(this.host.nativeElement, 'blur')
            .pipe(
                delay(200),
                takeUntil(this.destroy)
            )
            .subscribe(event => {
                this.hideDropdownMenu();
            });
    }

    ngOnDestroy(): void {
        this.destroy.next();
    }

    hideDropdownMenu() {
        if (this.dropdownRef) {
            this.dropdownRef.setInput('items', []);
            this.dropdownRef.setInput('mark', undefined);
        }
        if (this.typeaheadChanged) {
            const controlValue = this.ngControl.control.value;
            if (this.nlrTypehead) {
                let isFound = false;
                this.nlrTypehead(of(controlValue)).subscribe({
                    next: items => {
                        isFound = items.includes(controlValue);
                    },
                    complete: () => {
                        if (!isFound) {
                            this.ngControl.control.setValue('');
                        }
                    }
                });
            }
            this.typeaheadChanged = false;
        }
    }

    createNewDropdownAndInsertIntoContainer() {
        this.dropdownRef = this.viewContainerRef.createComponent(NlrDropdownComponent);
        this.dropdownRef.setInput('items', []);
        this.dropdownRef.setInput('mark', undefined);
        this.dropdownRef.instance.selectedItem.subscribe(value => {
            if (value) {
                this.ngControl.control.setValue(value);
                this.typeaheadChanged = false;
                this.hideDropdownMenu();
                if (this.itemSelected) {
                    this.itemSelected.emit(value);
                }
            }
        });
        const viewRef = this.viewContainerRef.insert(this.dropdownRef.hostView);
        this.dropdownViewRefIndex = this.viewContainerRef.indexOf(viewRef);
    }
}
